import { eventBus } from '@libs/core';
import { SystemEventTypeEnum, TViewCommonOption, TWidget } from '@libs/types';
import React, { useCallback, useEffect } from 'react';
import { TZendeskChat } from '../../types/data';
import { TButtonOption } from '../../types/options';
import { ButtonFlat } from '../Buttons/ButtonFlat';
import { ButtonRowView } from '../Buttons/ButtonRowView';

declare global {
    interface Window {
        zE?: (...args: unknown[]) => void;
    }
}

export type TZendeskChatViewProps = {
    uid?: string;
    widget?: TWidget<TZendeskChat, TButtonOption>;
    isFloatButton?: boolean;
    zendeskChatApiKey: string;
    label?: string;
    iconName?: string;
    margin?: TButtonOption['margin'];
} & Pick<TZendeskChat, 'zendeskToken' | 'extras' | 'conversationId'> &
    Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames' | 'styleName'>;

export const ZendeskChatView = ({
    uid,
    widget,
    isFloatButton = false,
    isDisabled,
    isHide,
    parentStyleNames,
    label,
    iconName,
    margin,
    conversationId,
    zendeskChatApiKey,
    zendeskToken,
    extras,
}: TZendeskChatViewProps) => {
    const handleClick = useCallback(() => {
        if (!isDisabled && window?.zE) {
            window?.zE('messenger', 'open');
        }
    }, [isDisabled]);

    const onScriptRef = useCallback(
        (node: HTMLScriptElement | null) => {
            if (!node) return;

            const setupZendesk = () => {
                if (zendeskToken && window?.zE) {
                    window.zE('messenger', 'loginUser', function (callback: (arg0: string) => void) {
                        callback(zendeskToken);
                    });
                    if (extras) {
                        const conversationFields = Object.entries(extras).map(([key, value]) => ({
                            id: key,
                            value: value,
                        }));
                        window.zE('messenger:set', 'conversationFields', conversationFields);
                    }
                    // Navigate to the target conversation once the messenger is actually open,
                    // avoiding a race between `open` and `navigation`.
                    window.zE('messenger:on', 'open', function () {
                        if (conversationId && window?.zE) {
                            window.zE('messenger:ui', 'navigation', {
                                screen: 'Conversation',
                                options: { conversationId },
                            });
                        }
                    });
                }
            };

            if (window?.zE) {
                setupZendesk();
                return;
            }

            node.addEventListener('load', setupZendesk);
            return () => {
                node.removeEventListener('load', setupZendesk);
            };
        },
        [zendeskToken, extras, conversationId],
    );

    useEffect(() => {
        if (widget?.internalId) {
            eventBus.subscribeOnSpecificWidget(SystemEventTypeEnum.EXECUTE, widget.internalId, () => {
                handleClick();
            });
        }

        return () => {
            if (widget?.internalId) {
                eventBus.unsubscribeSpecificWidget(SystemEventTypeEnum.EXECUTE, widget.internalId);
            }
        };
    }, [widget?.internalId, handleClick]);

    useEffect(() => {
        return () => {
            if (window?.zE) {
                window?.zE('messenger', 'close');
            }
        };
    }, []);

    return (
        <>
            {zendeskChatApiKey && <script async src={zendeskChatApiKey} id="ze-snippet" ref={onScriptRef} />}
            {isFloatButton ? (
                <ButtonFlat
                    label={label}
                    iconName={iconName}
                    onClick={handleClick}
                    isHide={isHide}
                    isDisabled={isDisabled}
                    marginBottom={margin?.bottom}
                    marginLeft={margin?.left}
                    marginRight={margin?.right}
                    marginTop={margin?.top}
                />
            ) : (
                <ButtonRowView
                    uid={uid}
                    widget={widget}
                    isHide={isHide}
                    isDisabled={isDisabled}
                    onClick={handleClick}
                    label={label}
                    iconName={iconName}
                    margin={margin}
                    parentStyleNames={parentStyleNames}
                />
            )}
        </>
    );
};
